// Draw by numbers (owner, 2026-09-30: "I just tried to draw a box 3000,6000 mm and its
// impossible without direct number inputs ... these should have been in the tool
// palette"). Pure builders, so the tests hold the geometry; the tool manager adds the
// result as one undoable command.
//
// World axes are the canvas axes: X grows to the right, Y grows DOWN the screen. The
// angle is the one the line readout already shows: 0° points right, 90° points down.
import { dir, type Vec } from "../core/geometry";

export type NumberShape =
  | { kind: "rect"; x: number; y: number; w: number; h: number }
  | { kind: "line"; x: number; y: number; length: number; angleDeg: number }
  | { kind: "circle"; x: number; y: number; r: number };

/** Why a set of numbers draws nothing, or null when they are good. */
export function numberShapeProblem(s: NumberShape): string | null {
  const bad = (v: number) => !Number.isFinite(v);
  if (bad(s.x) || bad(s.y)) return "Type the start point.";
  switch (s.kind) {
    case "rect":
      if (bad(s.w) || bad(s.h)) return "Type the width and the height.";
      if (s.w === 0 || s.h === 0) return "A rectangle needs a width and a height above zero.";
      return null;
    case "line":
      if (bad(s.length) || s.length <= 0) return "Type a length above zero.";
      if (bad(s.angleDeg)) return "Type the angle in degrees.";
      return null;
    case "circle":
      if (bad(s.r) || s.r <= 0) return "Type a radius above zero.";
      return null;
  }
}

/** The points of a rectangle or a line; the centre and radius of a circle. */
export function numberShapeGeometry(s: NumberShape): { points: Vec[]; closed: boolean } | { center: Vec; r: number } {
  if (s.kind === "circle") return { center: { x: s.x, y: s.y }, r: s.r };
  if (s.kind === "rect") {
    // A negative width or height draws to the left or upward from the corner.
    const x2 = s.x + s.w;
    const y2 = s.y + s.h;
    return { points: [{ x: s.x, y: s.y }, { x: x2, y: s.y }, { x: x2, y: y2 }, { x: s.x, y: y2 }], closed: true };
  }
  const d = dir(s.angleDeg);
  return { points: [{ x: s.x, y: s.y }, { x: s.x + d.x * s.length, y: s.y + d.y * s.length }], closed: false };
}

/** Width and height of a polyline's box, and the patch that changes them (Inspector). */
export function resizePoints(points: Vec[], w: number | null, h: number | null): Vec[] {
  let minX = Infinity,
    minY = Infinity,
    maxX = -Infinity,
    maxY = -Infinity;
  for (const p of points) {
    minX = Math.min(minX, p.x);
    minY = Math.min(minY, p.y);
    maxX = Math.max(maxX, p.x);
    maxY = Math.max(maxY, p.y);
  }
  const sx = w !== null && maxX - minX > 1e-9 ? w / (maxX - minX) : 1;
  const sy = h !== null && maxY - minY > 1e-9 ? h / (maxY - minY) : 1;
  // The top-left corner stays where it is; everything else scales from it.
  return points.map((p) => ({ x: minX + (p.x - minX) * sx, y: minY + (p.y - minY) * sy }));
}

/** The box of the drawn geometry, not of the stroke around it (what the Inspector shows). */
export function geometryBounds(e: { type: string; points?: Vec[]; center?: Vec; r?: number }): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (e.type === "polyline" && e.points?.length) {
    const xs = e.points.map((p) => p.x);
    const ys = e.points.map((p) => p.y);
    return { minX: Math.min(...xs), minY: Math.min(...ys), maxX: Math.max(...xs), maxY: Math.max(...ys) };
  }
  if (e.type === "circle" && e.center && typeof e.r === "number") {
    return { minX: e.center.x - e.r, minY: e.center.y - e.r, maxX: e.center.x + e.r, maxY: e.center.y + e.r };
  }
  return null;
}
